// Focused provider adapter for the recovery flow. The password mutation is
// bound to the captured PASSWORD_RECOVERY JWT (Authorization Bearer) — never
// to the ambient SDK session — using the standard public GoTrue REST API with
// the same configured project URL and anon key. No new secrets, no service
// key, no custom auth framework.
// Resolve the GoTrue user endpoint strictly: HTTPS (or loopback HTTP for local
// fixtures) with no embedded credentials — a malformed/relative base must never
// send the recovery JWT somewhere unexpected.
const resolveUserEndpoint = (): string => {
  const configured = process.env.SUPABASE_URL || process.env.REACT_APP_SUPABASE_URL;
  if (!configured) throw new Error('supabase url not configured');
  const base = new URL(configured); // throws on missing protocol / malformed input
  if (base.username || base.password) {
    throw new Error('supabase url must not contain credentials');
  }
  const loopback = ['localhost', '127.0.0.1', '[::1]'].includes(base.hostname);
  if (base.protocol !== 'https:' && !(base.protocol === 'http:' && loopback)) {
    throw new Error('supabase url must use https');
  }
  return new URL('/auth/v1/user', base).toString();
};

export const updatePasswordWithRecoveryToken = async (
  recoveryToken: string,
  password: string
): Promise<void> => {
  const apikey = process.env.SUPABASE_ANON_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY;
  // Fail closed before any network work if configuration or proof is missing.
  if (!recoveryToken) throw new Error('missing recovery token');
  if (!apikey) throw new Error('supabase anon key not configured');
  const endpoint = resolveUserEndpoint();
  const res = await fetch(endpoint, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${recoveryToken}`,
      apikey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    throw new Error(`password update failed (${res.status})`);
  }
};
